import { useEffect, useState } from "react";
import {
  PICTURE_LINE_MAX_CHARS,
  hasPictureInput,
  pictureOptions,
  type PictureChoices,
} from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { StyleSampleDialog } from "../components/StyleSampleDialog";
import { CharCounter, ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, useWork } from "../lib/api";
import { navigate } from "../lib/navigation";
import { styleSampleUrl } from "../lib/picture";
import { pathForStep } from "../lib/session";

/**
 * 그림 수업 화면 1 · 그림 고르기 (+ 1-B 그림 느낌 예시 창).
 *
 * 🔑 학생 대부분은 강사·담임이 대신 누른다(아름학교 · 태블릿). 칸은 전부 선택이다(2026-10-06) —
 *    칸 하나만 눌러도, 한 줄만 적어도 「그림 만들기」가 켜진다. 비운 칸은 AI 재량.
 * 🔑 보내는 것은 칸의 **키**뿐이다. 프롬프트는 서버가 조립한다.
 */

type Draft = Omit<PictureChoices, "line"> & { line: string };

type ChipGroup = "gender" | "expression" | "color" | "background" | "style";

function draftFrom(choices: PictureChoices | null | undefined): Draft {
  return choices ? { ...choices, line: choices.line ?? "" } : { line: "" };
}

function failureMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) return "그림을 시작하지 못했어요. 다시 눌러 주세요.";
  if (caught.code === "LIMIT_EXHAUSTED") return "그림 다시 만들기 횟수를 다 썼어요. 선생님께 요청해 주세요.";
  return caught.message;
}

export function PicturePickPage({ workId }: { workId: string }) {
  const query = useWork(workId);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [styleDialog, setStyleDialog] = useState<NonNullable<PictureChoices["style"]> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data && draft === null) setDraft(draftFrom(query.data.work.picture));
  }, [draft, query.data]);

  if (query.loading || (query.data && draft === null)) return <LoadingPage label="그림 고르기를 불러오는 중..." />;
  if (!query.data || query.error || !draft) {
    return <ErrorPage message={query.error?.message ?? "그림 고르기를 불러오지 못했습니다."} />;
  }
  const data = query.data;
  const cut = data.cuts[0];
  const hasPicture = Boolean(cut?.image_asset_id);
  const ready = hasPictureInput(draft);
  const regenBlocked = hasPicture && data.image_regen_remaining <= 0;

  const pick = <K extends keyof Draft>(key: K, value: Draft[K]): void => {
    setDraft((current) => current ? { ...current, [key]: value } : current);
  };
  /** 칸은 한 번 더 누르면 풀린다 — 비우면 AI 재량(D9). */
  const toggle = <K extends Exclude<keyof Draft, "line">>(key: K, value: NonNullable<Draft[K]>): void => {
    setDraft((current) => current ? { ...current, [key]: current[key] === value ? undefined : value } : current);
  };

  const make = async (): Promise<void> => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const line = draft.line.trim();
      await apiRequest<{ job_id: string; cut_id: string }>(
        `/api/works/${encodeURIComponent(workId)}/picture`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            ...(draft.gender ? { gender: draft.gender } : {}),
            ...(draft.expression ? { expression: draft.expression } : {}),
            ...(draft.color ? { color: draft.color } : {}),
            ...(draft.background ? { background: draft.background } : {}),
            ...(draft.style ? { style: draft.style } : {}),
            ...(draft.hair ? { hair: draft.hair } : {}),
            ...(draft.glasses ? { glasses: draft.glasses } : {}),
            ...(line ? { line } : {}),
          }),
        },
      );
      navigate(pathForStep(workId, "pic_image"));
    } catch (caught) {
      setError(failureMessage(caught));
      setBusy(false);
    }
  };

  const doneMark = (key: ChipGroup) => (
    <span className={draft[key] ? "pic-step-dot ok" : "pic-step-dot"}>{draft[key] ? "✓ 골랐어요" : "안 골라도 돼요"}</span>
  );

  return (
    <StudioShell currentStep={1} imageRegenRemaining={data.image_regen_remaining}>
      <div className="mv-page">
        <header className="mv-heading">
          <span className="mv-kicker">그림 1 · 그림 고르기</span>
          <h1 tabIndex={-1}>어떤 나를 그릴까요?</h1>
          <p>마음에 드는 것만 골라요. 한 줄만 적어도 AI가 나만의 그림을 그려 줘요.</p>
        </header>

        <section className="mv-card pic-pick-block">
          <div className="pic-pick-head"><h2>나는요</h2>{doneMark("gender")}</div>
          <div className="pic-pick-grid pic-pick-grid-2">
            {pictureOptions.gender.map((option) => (
              <button
                key={option.key}
                type="button"
                className={draft.gender === option.key ? "pic-pick selected" : "pic-pick"}
                aria-pressed={draft.gender === option.key}
                onClick={() => toggle("gender", option.key)}
              ><span className="pic-emoji" aria-hidden="true">{option.emoji}</span>{option.label}</button>
            ))}
          </div>
          <div className="pic-sub-row">
            <div>
              <h3>머리 모양 <small className="pic-opt">선택</small></h3>
              <div className="pic-mini-grid">
                {pictureOptions.hair.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={draft.hair === option.key ? "pic-mini selected" : "pic-mini"}
                    aria-pressed={draft.hair === option.key}
                    onClick={() => toggle("hair", option.key)}
                  ><span aria-hidden="true">{option.emoji}</span>{option.label}</button>
                ))}
              </div>
            </div>
            <div>
              <h3>안경 <small className="pic-opt">선택</small></h3>
              <div className="pic-mini-grid pic-mini-grid-2">
                {pictureOptions.glasses.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={draft.glasses === option.key ? "pic-mini selected" : "pic-mini"}
                    aria-pressed={draft.glasses === option.key}
                    onClick={() => toggle("glasses", option.key)}
                  ><span aria-hidden="true">{option.emoji}</span>{option.label}</button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mv-card pic-pick-block">
          <div className="pic-pick-head"><h2>표정</h2>{doneMark("expression")}</div>
          <div className="pic-pick-grid">
            {pictureOptions.expression.map((option) => (
              <button
                key={option.key}
                type="button"
                className={draft.expression === option.key ? "pic-pick selected" : "pic-pick"}
                aria-pressed={draft.expression === option.key}
                onClick={() => toggle("expression", option.key)}
              ><span className="pic-emoji" aria-hidden="true">{option.emoji}</span>{option.label}</button>
            ))}
          </div>
        </section>

        <section className="mv-card pic-pick-block">
          <div className="pic-pick-head"><h2>좋아하는 색</h2>{doneMark("color")}</div>
          <div className="pic-color-grid">
            {pictureOptions.color.map((option) => (
              <button
                key={option.key}
                type="button"
                className={draft.color === option.key ? "pic-color selected" : "pic-color"}
                aria-pressed={draft.color === option.key}
                onClick={() => toggle("color", option.key)}
              ><i aria-hidden="true" style={{ background: option.hex }} />{option.label}</button>
            ))}
          </div>
        </section>

        <section className="mv-card pic-pick-block">
          <div className="pic-pick-head"><h2>배경</h2>{doneMark("background")}</div>
          <div className="pic-pick-grid pic-pick-grid-6">
            {pictureOptions.background.map((option) => (
              <button
                key={option.key}
                type="button"
                className={draft.background === option.key ? "pic-pick selected" : "pic-pick"}
                aria-pressed={draft.background === option.key}
                onClick={() => toggle("background", option.key)}
              ><span className="pic-emoji" aria-hidden="true">{option.emoji}</span>{option.label}</button>
            ))}
          </div>
        </section>

        <section className="mv-card art-direction-card pic-pick-block">
          <div>
            <h2>그림 느낌</h2>
            <p>누르면 예시 그림을 보여 줘요. 보고 마음에 들면 골라요. 고른 걸 한 번 더 누르면 풀려요.</p>
            {doneMark("style")}
          </div>
          <div className="art-chip-grid">
            {pictureOptions.style.map((option) => (
              <button
                key={option.key}
                type="button"
                className={draft.style === option.key ? "selected" : ""}
                aria-pressed={draft.style === option.key}
                // 이미 고른 느낌을 다시 누르면 풀린다(다른 칸과 같다). 안 고른 느낌은 예시 창부터 연다.
                onClick={() => draft.style === option.key ? toggle("style", option.key) : setStyleDialog(option.key)}
              >{option.label}</button>
            ))}
          </div>
        </section>

        <section className="mv-card pic-pick-block">
          <div className="pic-pick-head"><h2>한 줄 더하기 <small className="pic-opt">선택</small></h2></div>
          <p className="pic-sub">선생님이 대신 적어 주셔도 돼요. 위에서 하나도 안 골라도 이 한 줄만으로 그림이 나와요.</p>
          <label className="sr-only" htmlFor="picture-line">한 줄 더하기</label>
          <textarea
            id="picture-line"
            className="pic-textarea"
            value={draft.line}
            maxLength={PICTURE_LINE_MAX_CHARS}
            placeholder="예: 강아지를 안고 있어요"
            onChange={(event) => pick("line", event.target.value)}
          />
          <CharCounter value={draft.line} max={PICTURE_LINE_MAX_CHARS} />
        </section>

        {error ? <InlineError>{error}</InlineError> : null}
        <footer className="mv-footer">
          <span>
            {!ready
              ? "하나만 고르거나 한 줄만 적어도 눌러져요"
              : regenBlocked
                ? "그림 다시 만들기 횟수를 다 썼어요. 선생님께 요청해 주세요."
                : hasPicture
                  ? `다시 그리면 다시 만들기 횟수를 1번 써요(남은 ${data.image_regen_remaining}번)`
                  : "준비됐어요! 그림을 만들어요"}
          </span>
          {hasPicture ? (
            <button className="button-secondary" type="button" disabled={busy} onClick={() => navigate(pathForStep(workId, "pic_image"))}>
              그림으로 돌아가기
            </button>
          ) : null}
          <button className="button-primary" type="button" disabled={!ready || busy || regenBlocked} onClick={() => void make()}>
            {busy ? <InlineSpinner label="시작하는 중..." /> : hasPicture ? "이걸로 다시 그리기" : "그림 만들기"}
          </button>
        </footer>
      </div>
      {styleDialog ? (
        <StyleSampleDialog
          kicker="그림 느낌 예시"
          label={styleOption(styleDialog).label}
          desc={styleOption(styleDialog).desc}
          imageUrl={styleSampleUrl(styleDialog)}
          onClose={() => setStyleDialog(null)}
          onConfirm={() => {
            pick("style", styleDialog);
            setStyleDialog(null);
          }}
        />
      ) : null}
    </StudioShell>
  );
}

function styleOption(style: NonNullable<PictureChoices["style"]>) {
  return pictureOptions.style.find((candidate) => candidate.key === style) ?? pictureOptions.style[0];
}
